/**
 * @fileoverview Core event types for the LOCI platform
 *
 * This module defines the central event-related types used across all LOCI services.
 * These types are derived from the Zod validation schemas but optimized for runtime usage.
 *
 * @author LOCI Development Team
 * @since 1.0.0
 */

/**
 * Geographic coordinates interface
 * Used for spatial queries and location-based features
 */
export interface Coordinates {
  /** Latitude value between -90 and 90 */
  latitude: number;
  /** Longitude value between -180 and 180 */
  longitude: number;
}

/**
 * Event category enumeration
 * Maps to the validation schema categories.
 * Canonical definition lives in `./common/index.ts`; re-exported here so
 * that modules importing from `./event` continue to resolve it.
 */
export type { EventCategory } from './common';
import type { EventCategory } from './common';

/**
 * Canonical lifecycle-status members, kept in exact parity with the Prisma
 * `EventStatus` enum (`packages/db-core/prisma/schema.prisma`).
 *
 * This is the single source of truth for both the {@link EventStatus} union and
 * the runtime Zod enum in `@loci/validation` (`createEventSchema`), so the two
 * can never drift from each other. `@loci/types` deliberately does NOT import
 * the generated Prisma client (it depends only on date-fns and is consumed by
 * the web/mobile builds — pulling in `@prisma/client` would invert the
 * dependency graph). Parity with Prisma is instead enforced at compile time by
 * the assertion in `services/backend-api/src/utils/browseEventStatus.ts`
 * (API-1618), which fails the build the moment the two diverge.
 *
 * Declared `as const` (not `readonly EventStatus[]`) so `(typeof … )[number]`
 * resolves to the exact literal union rather than collapsing to `string` — the
 * widening that makes a parity check a vacuous truth.
 */
export const EVENT_LIFECYCLE_STATUSES = [
  'DRAFT',
  'ACTIVE',
  'CANCELLED',
  'COMPLETED',
  'POSTPONED',
  'SOLD_OUT',
  'OFFSALE',
] as const;

/**
 * Event status enumeration
 * Represents the lifecycle state of an event.
 *
 * Derived from {@link EVENT_LIFECYCLE_STATUSES}; mirrors the Prisma `EventStatus`
 * enum exactly. Distinct from `DiscoverEventStatus` (`./discover`), the narrow
 * browse-visible subset (`'ACTIVE' | 'SOLD_OUT'`) — do not merge them.
 */
export type EventStatus = (typeof EVENT_LIFECYCLE_STATUSES)[number];

/**
 * Venue type enumeration
 * Categorizes different types of event venues
 */
export type VenueType =
  | 'CONCERT_HALL'
  | 'PARK'
  | 'STADIUM'
  | 'THEATER'
  | 'CLUB'
  | 'RESTAURANT'
  | 'BAR'
  | 'CONFERENCE_CENTER'
  | 'HOTEL'
  | 'MUSEUM'
  | 'GALLERY'
  | 'OUTDOOR_SPACE'
  | 'COMMUNITY_CENTER'
  | 'SCHOOL'
  | 'UNIVERSITY'
  | 'SPORTS_FACILITY'
  | 'SHOPPING_CENTER'
  | 'ARENA'
  | 'PLACE_OF_WORSHIP'
  | 'EVENT_SPACE'
  | 'LIBRARY'
  | 'CINEMA'
  | 'OTHER';

/**
 * Venue information interface
 * Contains all venue-related data
 */
export interface Venue {
  /** Unique venue identifier (optional for new venues) */
  id?: string;
  /** Display name of the venue */
  name: string;
  /** Full street address */
  address: string;
  /** City name */
  city: string;
  /** State/province code (2 characters for US states) */
  state?: string;
  /** Country code (ISO 2-3 character code) */
  country: string;
  /** Geographic coordinates */
  coordinates: Coordinates;
  /** Maximum capacity (optional) */
  capacity?: number;
  /** Venue type classification */
  type?: VenueType;
  /** Website URL (optional) */
  website?: string;
  /** Contact phone number (optional) */
  phone?: string;
}

/**
 * Artist/performer social media links
 */
export interface ArtistSocial {
  /** Instagram handle or URL */
  instagram?: string;
  /** Twitter handle or URL */
  twitter?: string;
  /** Facebook page URL */
  facebook?: string;
  /** Spotify artist URL */
  spotify?: string;
  /** Artist website URL */
  website?: string;
}

/**
 * PIP-146 — CC BY-SA provenance for an enriched artist bio.
 *
 * When an `Artist.description` was enriched from a real published source
 * (Wikipedia via MusicBrainz/Wikidata), this blob records everything the read
 * side needs to render an honest, correctly-licensed credit. CC BY-SA requires
 * a visible source credit + link, so a bio MUST NOT be rendered as enriched
 * content without it. Persisted verbatim in `Artist.descriptionAttribution`.
 */
export interface DescriptionAttribution {
  /** Human-readable source name, e.g. "Wikipedia". */
  source: string;
  /** Canonical URL of the source article (the required attribution link). */
  sourceUrl: string;
  /** License identifier, e.g. "CC BY-SA 4.0". */
  license: string;
  /** Source article revision the text was taken from, when known. */
  revision?: string;
  /** ISO-8601 instant the text was retrieved. */
  retrievedAt: string;
}

/**
 * Image URLs for different sizes
 */
export interface ImageUrls {
  /** Thumbnail image URL (small) */
  thumbnail?: string;
  /** Medium image URL */
  medium?: string;
  /** Large image URL */
  large?: string;
}

/**
 * Artist external source enumeration
 */
export type ArtistExternalSource = 'ticketmaster' | 'manual';

/**
 * Artist/performer information interface
 */
export interface Artist {
  /** Unique artist identifier (optional for new artists) */
  id?: string;
  /** Artist or performer name */
  name: string;
  /** Primary genre (optional, deprecated - use genres array) */
  genre?: string;
  /** Multiple genre classifications */
  genres?: string[];
  /** Artist website URL (optional, deprecated - use social.website) */
  website?: string;
  /** Social media links (optional) */
  social?: ArtistSocial;
  /** Biographical description */
  description?: string;
  /**
   * PIP-146 — CC BY-SA provenance when `description` was enriched from a real
   * published source. Null/absent for manual or unenriched descriptions.
   */
  descriptionAttribution?: DescriptionAttribution | null;
  /** Image URL (optional, deprecated - use imageUrls) */
  imageUrl?: string;
  /** Multiple image URLs for different sizes */
  imageUrls?: ImageUrls;
  /** External source ID for deduplication */
  externalId?: string;
  /** Data source identifier */
  externalSource?: ArtistExternalSource;
}

/**
 * Core event interface
 * This is the primary event type used throughout the application
 */
export interface Event {
  /** Unique event identifier */
  id: string;
  /** Event title/name */
  title: string;
  /** Detailed event description (minimum 10 characters) */
  description: string;

  /** Event start date (ISO 8601 string) */
  date: string;
  /** Event start time (optional, format varies) */
  startTime?: string;
  /** Event end time (optional, format varies) */
  endTime?: string;
  /** Timezone identifier (e.g., "America/New_York") */
  timezone: string;

  /** Event category */
  category: EventCategory;
  /** Additional tags for categorization */
  tags: string[];

  /** Venue information */
  venue: Venue;
  /** Human-readable location description */
  location: string;

  /** Legacy coordinates for backward compatibility */
  latitude: number;
  /** Legacy coordinates for backward compatibility */
  longitude: number;

  /** List of performing artists */
  artists: Artist[];

  /** Ticket purchase URL (optional) */
  ticketUrl?: string;
  /** Price in string format (e.g., "$25.00") */
  price?: string;
  /** Number of available tickets (optional) */
  availableTickets?: number;
  /** Whether the event is sold out */
  soldOut: boolean;

  /** Event lifecycle status */
  status: EventStatus;
  /** Whether the event has been verified */
  verified: boolean;
  /** Featured/promoted status */
  featured: boolean;

  /** Event image URL (optional) */
  imageUrl?: string;
  /** Age restriction information (optional) */
  ageRestriction?: string;
  /** Accessibility information (optional) */
  accessibility?: string;

  /** Data source identifier (optional) */
  sourceId?: string;
  /** Original event ID from data source (optional) */
  sourceEventId?: string;
  /** Source URL where event was found (optional) */
  sourceUrl?: string;

  /** Associated location ID (optional) */
  locationId?: string;
  /** Modern coordinates field (optional) */
  coordinates?: Coordinates;

  /** Record creation timestamp */
  createdAt: string;
  /** Record last update timestamp */
  updatedAt: string;
}

/**
 * Event creation interface
 * Used for API endpoints that create new events
 */
export interface CreateEvent {
  /** Event title/name */
  title: string;
  /** Event description (optional, minimum 10 characters if provided) */
  description?: string;
  /** Event start date */
  date: Date;
  /** Event end date (optional) */
  endDate?: Date;
  /** Event image URL (optional) */
  imageUrl?: string;
  /** Ticket purchase URL (optional) */
  ticketUrl?: string;
  /** Price as number (optional) */
  price?: number;
  /** Currency code (3 characters, defaults to USD) */
  currency: string;
  /** Age restriction information (optional) */
  ageRestriction?: string;
  /** Accessibility information (optional) */
  accessibility?: string;
  /** Event tags */
  tags: string[];
  /** Event status */
  status: EventStatus;
  /** Timezone identifier */
  timezone: string;
  /** Data source identifier (optional) */
  sourceId?: string;
  /** Original event ID from source (optional) */
  sourceEventId?: string;
  /** Source URL (optional) */
  sourceUrl?: string;
  /** Associated location ID (optional) */
  locationId?: string;
  /** Geographic coordinates (optional) */
  coordinates?: Coordinates;
}

/**
 * Display-optimized event interface
 * Used by frontend components for rendering events
 */
export interface DisplayEvent {
  /** Unique event identifier */
  id: string;
  /** Event title */
  title: string;
  /** Event description */
  description: string;
  /** Event image URL */
  image: string;
  /** Formatted date string */
  date: string;
  /** Formatted time string */
  time: string;
  /** Human-readable location */
  location: string;
  /** Distance from user (formatted string) */
  distance: string;
  /** Formatted price string */
  price: string;
  /** Number of attendees/interested users */
  attendees: number;
  /** Event rating (0-5 scale) */
  rating: number;
  /** Event category */
  category: string;

  /** Ticket availability status */
  ticketAvailability?: 'few-left' | 'selling-fast' | 'available' | 'sold-out';
  /** Urgency message for tickets */
  urgencyStatus?: string;
  /** Number of user's friends attending */
  friendsGoing?: number;
  /** Venue capacity (optional) */
  venueCapacity?: number;

  /** Weather condition for outdoor events */
  weatherCondition?: 'sunny' | 'rainy' | 'cloudy' | 'indoor';
  /** Hours until event starts */
  startsInHours?: number;
  /** Whether event is outdoors */
  isOutdoor?: boolean;

  /** Price range display (e.g., "From $25") */
  priceRange?: string;
  /** Available ticket tiers */
  ticketTiers?: string[];

  /** Trending indicator for popular events */
  trendingIndicator?: {
    icon: 'flame' | 'star' | 'sparkles';
    label: 'Hot' | 'Popular' | 'New';
    color?: string;
    description?: string;
    tone?: 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'neutral';
  };
}

/**
 * Event validation result interface
 * Used by the validation system for event processing
 */
export interface EventValidationResult {
  /** Whether the event passed validation */
  isValid: boolean;
  /** Successfully parsed event data (if valid) */
  parsedEvent?: Event;
  /** Schema validation errors */
  schemaErrors: string[];
  /** Business logic validation errors */
  validationErrors: string[];
  /** Non-critical warnings */
  warnings: string[];
  /** Data quality score (0-1) */
  qualityScore: number;
  /** Potential duplicate events found */
  duplicates: unknown[];
  /** Whether manual review is recommended */
  needsReview: boolean;
}

/**
 * Data quality metrics interface
 * Used for monitoring ingestion pipeline health
 */
export interface DataQualityMetrics {
  /** Total number of events processed */
  totalEvents: number;
  /** Number of valid events */
  validEvents: number;
  /** Number of invalid events */
  invalidEvents: number;
  /** Number of duplicates detected */
  duplicatesFound: number;
  /** Number of duplicates removed */
  duplicatesRemoved: number;
  /** Average confidence score (0-1) */
  averageConfidence: number;
  /** Number of different data sources */
  sourcesCount: number;
  /** Last processing timestamp */
  lastProcessed: string;
}

/**
 * Duplicate detection result interface
 * Used for identifying and handling duplicate events
 */
export interface DuplicateDetectionResult {
  /** Whether a duplicate was detected */
  isDuplicate: boolean;
  /** Confidence level of the match (0-1) */
  confidence: number;
  /** The matched event (if duplicate) */
  matchedEvent?: Event;
  /** Reasons for the duplicate classification */
  reasons: string[];
  /** Recommended action to take */
  action: 'merge' | 'replace' | 'ignore' | 'manual_review';
}

/**
 * Manual review queue item interface
 * Used for items requiring human review
 */
export interface ManualReviewItem {
  /** Unique review item identifier */
  id: string;
  /** Original event data */
  originalEvent: Event;
  /** Potential duplicate event */
  potentialDuplicate: Event;
  /** Match confidence score */
  confidence: number;
  /** Reasons for flagging */
  reasons: string[];
  /** Review status */
  status: 'pending' | 'approved' | 'rejected' | 'merged';
  /** Reviewer identifier (optional) */
  reviewedBy?: string;
  /** Review timestamp (optional) */
  reviewedAt?: string;
  /** Creation timestamp */
  createdAt: string;
}
